/**
 * formatLogDetails — Traductor de detalles técnicos de log a lenguaje humano
 *
 * Qué hace: Convierte la combinación action + details (JSON) de un log
 *           en texto legible para cualquier usuario sin conocimientos técnicos.
 *           Soporta todos los módulos: auth, caja, ventas, inventario, personal.
 * Qué exporta: formatDetailsSummary (resumen corto), formatDetailsReadable (detalle completo)
 * Qué NO debe hacer: No acceder a APIs ni contener lógica de UI/componentes.
 */

import { getDefaultPaymentMethodLabel } from "@/lib/paymentMethodsConfig";

/** Formatea un valor numérico como moneda COP */
const money = (val: unknown): string => {
  const n = Number(val);
  if (isNaN(n)) return String(val);
  return `$${n.toLocaleString("es-CO")}`;
};

/**
 * Genera un resumen corto (1 línea) legible de los detalles del log.
 * Si el log ya trae `resumen` del backend, úsalo directamente.
 * Esta función es el fallback para logs sin campo resumen.
 */
export const formatDetailsSummary = (
  action: string,
  details: Record<string, unknown>,
): string => {
  switch (action) {
    /* ── Auth ── */
    case "login": {
      const metodo = String(details.metodoLogin || "").toLowerCase();
      let label = "Email";
      if (metodo.includes("apple")) label = "Apple";
      else if (metodo.includes("google")) label = "Google";
      return `Inicio de sesión (${label})`;
    }
    case "login_failed":
      return `Intento de acceso fallido${details.intentosFallidos ? ` — intento #${details.intentosFallidos}` : ""}${details.motivo ? `: ${details.motivo}` : ""}`;
    case "logout":
      return "Cierre de sesión";

    /* ── Ventas ── */
    case "venta_creada":
    case "caja_venta_completada":
      return `Nueva venta por ${money(details.total)}${details.metodoPago ? ` — Pago con ${details.metodoPago}` : ""}`;

    case "caja_venta_credito":
      return `Venta a crédito registrada: ${money(details.total)} — Cliente: ${details.clienteNombre || "Sin nombre"}`;

    case "cliente_registrado":
      return `Nuevo cliente registrado: ${details.nombre}${details.telefono ? ` — Tel: ${details.telefono}` : ""}`;

    case "caja_descuento_aplicado": {
      const monto = details.monto ?? details.descuento ?? 0;
      const pct = details.porcentaje ? ` (${details.porcentaje}%)` : "";
      return `Descuento global aplicado${pct}: -${money(monto)}`;
    }

    case "caja_servicio_registrado": {
      const monto = details.monto ?? details.servicio ?? 0;
      const pct = details.porcentaje ? ` (${details.porcentaje}%)` : "";
      return `Propina/Servicio registrado${pct}: +${money(monto)}`;
    }

    case "caja_item_ajustado": {
      const displayMonto = details.montoDescuento ?? details.descuento ?? 0;
      const displayAjuste = details.tipoAjuste ?? details.ajuste ?? "Descuento";
      return `Ajuste en "${details.producto}": ${displayAjuste} (-${money(displayMonto)})`;
    }
    case "caja_producto_carrito_eliminado": {
      const valor = details.valorLinea ?? 0;
      const autorizado = details.autorizadoPor
        ? ` — Autorizado por ${details.autorizadoPor}`
        : details.requirioAutorizacion
          ? " — Requería autorización"
          : "";
      return `Producto eliminado del carrito: ${details.producto || ""} (-${money(valor)})${autorizado}`;
    }
    case "seguridad_panico_activado": {
      const motivo = details.motivo || "Sin mensaje";
      return `Alerta de seguridad activada — Motivo: ${motivo}`;
    }
    case "caja_venta_espera_eliminada": {
      const autorizado = details.autorizadoPor
        ? ` — Autorizado por ${details.autorizadoPor}`
        : details.requirioAutorizacion
          ? " — Requería autorización"
          : "";
      return `Venta en espera eliminada: ${details.ventaEnEspera || ""}${autorizado}`;
    }
    case "caja_producto_venta_espera_eliminado": {
      const autorizado = details.autorizadoPor
        ? ` — Autorizado por ${details.autorizadoPor}`
        : details.requirioAutorizacion
          ? " — Requería autorización"
          : "";
      return `Producto eliminado de venta en espera: ${details.producto || ""} (-${money(details.valorLinea ?? 0)})${autorizado}`;
    }
    case "venta_eliminada":
      return `Venta ${details.ventaId || ""} borrada${details.motivo ? `: ${details.motivo}` : ""}`;
    case "venta_editada":
      return `Venta ${details.ventaId || ""} modificada`;

    /* ── Caja ── */
    case "caja_abierta":
      return `Inicio de turno con base de ${money(details.montoInicial)}${details.turno ? ` — Turno ${details.turno}` : ""}`;
    case "caja_punto_creado":
      return "Punto de venta creado exitosamente";
    case "caja_punto_editado":
      return "Punto de venta actualizado (Nombre/Alias)";
    case "caja_punto_eliminado":
      return "Punto de venta eliminado";
    case "caja_arqueo_iniciado":
      return "Inicio de conteo (Apertura de gaveta)";
    case "caja_arqueo_fallido":
      return `Intento de arqueo fallido (#${details.intento || "?"})`;
    case "caja_reporte_parcial_pedido":
      return "Solicitud de reporte parcial";
    case "caja_reporte_parcial_visto":
      return "Reporte parcial visualizado";
    case "caja_reporte_parcial_compartido":
      return "Reporte parcial compartido";

    /* ── Movimientos de Caja ── */
    case "caja_movimiento_pedido":
      return `Solicitud de ${String(details.tipo || "movimiento").toLowerCase()}`;
    case "caja_retiro_registrado":
      return `Retiro de efectivo: ${money(details.monto)}`;
    case "caja_gasto_registrado":
      return `Gasto registrado: ${money(details.monto)}${details.vueltoPendiente ? " (Vuelto pendiente)" : ""}`;
    case "caja_ingreso_registrado":
      return `Ingreso de dinero: ${money(details.monto)} (${details.metodoPago || "Efectivo"})`;
    case "caja_gasto_cierre_pedido":
      return "Solicitud de costo real (Cierre de gasto)";
    case "caja_gasto_cierre_finalizado": {
      const modal =
        details.resultadoEfectivo === "vuelto"
          ? " (Vuelto a caja)"
          : details.resultadoEfectivo === "faltante"
            ? " (Faltante en caja)"
            : " (Exacto)";
      return `Gasto finalizado: ${money(details.montoReal)}${modal}`;
    }

    case "caja_cerrada": {
      const diff = Number(details.diferencia);
      const diffText = isNaN(diff)
        ? ""
        : diff < 0
          ? ` (Faltó dinero: ${money(Math.abs(diff))})`
          : diff > 0
            ? ` (Sobró dinero: ${money(diff)})`
            : " (Todo cuadrado)";
      return `Cierre de caja: ${money(details.montoCierre)}${diffText}${details.duracionTurno ? ` — Duró ${details.duracionTurno}` : ""}`;
    }

    /* ── Inventario ── */
    case "producto_creado":
      return `Nuevo producto: ${details.nombre || details.productoId || ""}${details.precio ? ` — Precio: ${money(details.precio)}` : ""}`;
    case "producto_editado":
      return `Datos actualizados: ${details.nombre || ""}${details.campo ? ` (${details.campo})` : ""}`;
    case "producto_eliminado":
      return `Producto borrado: ${details.nombre || ""}${details.motivo ? ` — Motivo: ${details.motivo}` : ""}`;
    case "stock_ajustado":
      return `Stock cambió: ${details.cantidadAnterior ?? "?"} → ${details.cantidadNueva ?? "?"}${details.motivo ? ` (${details.motivo})` : ""}`;

    /* ── Personal ── */
    case "empleado_creado":
      return `Nuevo empleado: ${details.nombre || ""}${details.rol ? ` — ${details.rol}` : ""}`;
    case "empleado_editado":
      return `Empleado modificado: ${details.nombre || ""}`;
    case "empleado_eliminado":
      return `Empleado eliminado: ${details.nombre || ""}`;
    case "salario_actualizado":
      return `Salario actualizado: ${details.nombre || ""}${details.porcentajeAumento ? ` (${details.porcentajeAumento})` : ""}`;
    case "permisos_modificados":
      return `Permisos modificados: ${details.nombre || ""}${details.autorizadoPor ? ` — ${details.autorizadoPor}` : ""}`;
    case "check_in":
      return `Entrada a las ${details.horaEntrada || "?"}${details.minutosRetraso ? ` — ${details.minutosRetraso} min tarde` : " — Puntual"}`;
    case "check_out":
      return `Salida${details.horaSalida ? ` a las ${details.horaSalida}` : ""}${details.totalHoras ? ` — ${details.totalHoras}` : ""}`;
    case "tarea_creada":
      return `Tarea: ${details.titulo || ""}${details.prioridad ? ` (prioridad ${details.prioridad})` : ""}`;

    /* ── IA Insights ── */
    case "ia_detection":
      return `Aviso: ${details.hallazgo || ""}${details.probabilidad ? ` (prob: ${details.probabilidad})` : ""}`;
    case "session_hijack_detected":
      return `Acceso sospechoso: ${details.motivo || "Cambio inusual de ubicación"}`;

    /* ── Higiene ── */
    case "check_limpieza":
      return `Limpieza${details.zona ? ` de ${details.zona}` : ""} — ${details.resultado === "approved" ? "Hecho" : details.resultado || ""}`;

    /* ── Métricas ── */
    case "metricas_consultadas":
      return `Revisión de: ${details.reporte || ""}${details.periodo ? ` (${details.periodo})` : ""}`;

    /* ── Seguridad (Administrativo) ── */
    case "employee_enabled":
      return `Acceso al sistema habilitado para ${details.empleadoNombre || ""}`.trim();
    case "employee_disabled":
      return "Empleado inhabilitado";
    case "credential_disabled":
      return "Credencial inhabilitada";
    case "credential_regenerated":
      return "Regeneración de carnet";
    case "credential_printed":
      return "Impresión de carnet";
    case "forgot_password_request":
      return "Solicitud de recuperación de contraseña";
    case "password_reset_success":
      return "Contraseña restablecida con éxito";
    case "password_reset_request_self":
      return "Solicitud propia de cambio de contraseña";
    case "session_revoked":
      return `Sesión cerrada por seguridad (Sesión: ${details.sessionId || ""})`;
    case "ip_blocked":
      return "Acceso bloqueado por seguridad";

    /* ── Tienda Virtual ── */
    case "pedido_online":
      return `Pedido online ${details.entidadId || ""}${details.total ? ` por ${money(details.total)}` : ""}`;

    /* ── Calificaciones ── */
    case "nueva_calificacion":
      return `Calificación ${details.estrellas ? `${details.estrellas} ★` : ""}${details.comentario ? `: ${details.comentario}` : ""}`;
    case "calificacion_archivada":
      return `Calificación archivada por el administrador`;

    /* ── Facturación / Ventas ── */
    case "facturacion_venta_anulada":
      return `Venta anulada${details.ventaId ? ` #${details.ventaId}` : ""}${details.motivo ? ` — ${details.motivo}` : ""}`;
    case "facturacion_devolucion_procesada":
      return `Devolución procesada${details.ventaId ? ` #${details.ventaId}` : ""}${details.monto ? ` — ${money(details.monto)}` : ""}`;
    case "tesoreria_movimiento_anulado":
      return `Movimiento anulado${details.tipo ? ` (${details.tipo})` : ""}${details.motivo ? ` — ${details.motivo}` : ""}`;

    /* ── Créditos ── */
    case "credito_abono_registrado":
      return `Abono registrado${details.cliente ? ` — ${details.cliente}` : ""}${details.monto ? `: ${money(details.monto)}` : ""}`;

    /* ── Fidelización ── */
    case "fidelizacion_config_guardada":
      return "Configuración del programa de puntos actualizada";
    case "fidelizacion_premio_creado":
      return `Premio creado: ${details.nombre || details.premio || ""}`;
    case "fidelizacion_premio_editado":
      return `Premio editado: ${details.nombre || details.premio || ""}`;
    case "fidelizacion_premio_eliminado":
      return `Premio eliminado: ${details.nombre || details.premio || ""}`;
    case "fidelizacion_sorteo_realizado":
      return `Sorteo realizado${details.ganadores ? ` — ${details.ganadores} ganador(es)` : ""}`;

    /* ── Inventario ── */
    case "inventario_stock_agotado":
      return `Stock agotado: ${details.nombre || details.productoId || ""}`;
    case "inventario_stock_repuesto":
      return `Stock repuesto: ${details.nombre || details.productoId || ""}${details.cantidad ? ` (+${details.cantidad})` : ""}`;
    case "inventario_precios_masivos":
      return `Actualización masiva de precios${details.cantidad ? ` — ${details.cantidad} productos` : ""}`;
    case "inventario_conteo_ajustado":
      return `Conteo físico aplicado${details.aplicadas ? ` — ${details.aplicadas} ajuste(s)` : ""}${details.valorNeto ? ` — Valor neto: ${money(details.valorNeto)}` : ""}`;
    case "inventario_conteo_exportado":
      return `Reporte de conteo exportado${details.formato ? ` (${details.formato})` : ""}`;

    /* ── Personal ── */
    case "personal_empleado_creado":
      return `Nuevo empleado: ${details.nombre || ""}${details.rol ? ` — ${details.rol}` : ""}`;
    case "personal_empleado_editado":
      return `Empleado actualizado: ${details.nombre || ""}`;
    case "personal_empleado_eliminado":
      return `Empleado eliminado: ${details.nombre || ""}`;

    /* ── Tareas ── */
    case "personal_tarea_creada":
      return `Nueva tarea: ${details.titulo || ""}${details.asignadoA ? ` — ${details.asignadoA}` : ""}`;
    case "personal_tarea_editada":
      return `Tarea editada: ${details.titulo || ""}`;
    case "personal_tarea_eliminada":
      return `Tarea eliminada: ${details.titulo || ""}`;
    case "personal_tarea_estado_cambiado":
      return `Tarea ${details.estado || "actualizada"}: ${details.titulo || details.entidadId || ""}`;

    /* ── Asistencia ── */
    case "personal_asistencia_entrada":
      return `Entrada registrada${details.metodo ? ` (${details.metodo})` : ""}`;
    case "personal_asistencia_salida":
      return `Salida registrada${details.metodo ? ` (${details.metodo})` : ""}`;

    /* ── Nómina ── */
    case "nomina_empleado_liquidado":
      return `Nómina guardada: ${details.nombre || details.entidadId || ""}${details.periodo ? ` — ${details.periodo}` : ""}`;

    /* ── Turnos ── */
    case "personal_turno_asignado":
      return `Turno asignado${details.tipo ? ` (${details.tipo})` : ""}${details.fecha ? ` el ${details.fecha}` : ""}`;

    /* ── Vacantes ── */
    case "personal_vacante_creada":
      return `Vacante publicada: ${details.titulo || details.entidadId || ""}`;
    case "personal_vacante_editada":
      return `Vacante actualizada: ${details.titulo || details.entidadId || ""}`;
    case "personal_vacante_eliminada":
      return `Vacante eliminada: ${details.titulo || details.entidadId || ""}`;
    case "personal_postulacion_estado_cambiado":
      return `Postulación ${details.estado || "actualizada"}`;

    /* ── Buzón del equipo ── */
    case "personal_buzon_mensaje_publicado":
      return "Mensaje anónimo publicado en buzón del equipo";

    /* ── Higiene ── */
    case "higiene_area_creada":
      return `Área de higiene creada: ${details.nombre || details.entidadId || ""}`;
    case "higiene_area_editada":
      return `Área de higiene editada`;
    case "higiene_area_eliminada":
      return `Área de higiene eliminada`;
    case "higiene_item_creado":
      return `Ítem de higiene creado: ${details.nombre || details.entidadId || ""}`;
    case "higiene_item_editado":
      return `Ítem de higiene editado`;
    case "higiene_item_eliminado":
      return `Ítem de higiene eliminado`;
    case "higiene_registro_guardado":
      return `Registro de higiene ${details.estado || "guardado"}${details.fecha ? ` — ${details.fecha}` : ""}`;

    /* ── Tienda ── */
    case "tienda_pedido_estado_cambiado":
      return `Pedido ${details.estado || "actualizado"}: ${details.entidadId || ""}`;
    case "tienda_pedido_anulado":
      return `Pedido anulado${details.motivo ? ` — ${details.motivo}` : ""}`;
    case "tienda_pedido_devolucion":
      return `Devolución de pedido${details.monto ? ` — ${money(details.monto)}` : ""}`;
    case "tienda_config_guardada":
      return "Configuración de tienda actualizada";

    /* ── Fallback ── */
    default:
      return action.replace(/_/g, " ");
  }
};

/** Etiquetas legibles para las claves técnicas del JSON */
const FIELD_LABELS: Record<string, string> = {
  // Auth
  ip: "Conexión de internet utilizada",
  email: "Correo",
  metodoLogin: "Método de ingreso",
  socialProvider: "Método de ingreso",
  provider: "Método de ingreso",
  dispositivo: "Tipo de pantalla (Móvil / Computador)",
  userAgent: "Dispositivo / Equipo utilizado",
  sessionId: "Código de referencia",
  motivo: "Razón",
  intentosFallidos: "Veces que falló",
  cuentaBloqueada: "Acceso bloqueado",
  // Ventas / Caja
  ventaId: "Código de referencia",
  cantidadProductos: "Cantidad de productos",
  subtotalBruto: "Subtotal bruto (sin descuentos)",
  subtotalNeto: "Subtotal neto (con descuentos)",
  descuento: "Descuento",
  descuentoGeneral: "Descuento general",
  descuentoItems: "Descuento total ítems",
  servicio: "Propina / Servicio",
  ajustesProductos: "Ajustes de precio aplicados",
  impuesto: "Impuestos",
  total: "Total cobrado",
  totalVenta: "Total de la factura",
  metodoPago: "Cómo pagó",
  monto: "Valor del ajuste",
  producto: "Nombre del producto",
  sku: "SKU / Código",
  cantidad: "Cantidad eliminada",
  unidad: "Unidad",
  precioUnitario: "Precio unitario",
  valorLinea: "Valor de la línea",
  variante: "Variante / Atributos",
  caja: "Caja utilizada",
  sesionId: "Código de referencia de la sesión",
  ventaReferencia: "Venta de referencia",
  ventaEnEspera: "Venta en espera",
  requirioAutorizacion: "Requirió autorización",
  conMensaje: "Se envió con mensaje",
  estadoAlerta: "Estado de la alerta",
  itemsEnCarrito: "Productos en el carrito (referencia)",
  totalCarrito: "Total del carrito (referencia)",
  tipoAjuste: "Tipo de cambio aplicado",
  montoDescuento: "Dinero descontado",
  precioOriginal: "Precio normal",
  precioFinal: "Precio cobrado",
  cliente: "Cliente",
  clienteNombre: "Nombre del cliente beneficiario",
  saldoPendiente: "Deuda total pendiente",
  items: "Productos vendidos",
  telefono: "Teléfono de contacto",
  direccion: "Dirección registrada",
  itemsCount: "Cantidad de productos",
  turno: "Jornada",
  montoInicial: "Dinero base en caja",
  montoCierre: "Dinero al cerrar",
  diferenciaCaja: "Sobra/Falta dinero",
  cantidadVentas: "Ventas hechas",
  promedioVenta: "Venta promedio",
  duracionTurno: "Tiempo trabajado",
  desglosePago: "Detalle de pagos",
  verificadoPor: "Revisado por",
  terminal: "Caja registradora",
  responsable: "Persona a cargo",
  autorizadoPor: "Quien dio permiso",
  productosRestaurados: "Productos devueltos",
  productosAfectados: "Productos cambiados",
  // Movimientos de Caja
  montoReal: "Costo real pagado",
  montoSalida: "Dinero que salió de caja",
  vueltoMonto: "Vuelto devuelto a caja",
  faltanteMonto: "Dinero faltante",
  resultadoEfectivo: "Estado del cuadre",
  montoRealIndicado: "Costo declarado",
  // Inventario
  productoId: "Código de referencia",
  nombre: "Nombre del registro",
  alias: "Alias asignado",
  codigoBarras: "Código de barras",
  categoria: "Categoría",
  precio: "Precio de venta",
  costo: "Precio de compra",
  stock: "Cantidad actual",
  stockMinimo: "Mínimo permitido",
  unidadMedida: "Se vende por",
  proveedor: "Proveedor",
  tipo: "Tipo de cambio",
  cantidadAnterior: "Había",
  cantidadNueva: "Quedó en",
  diferencia: "Cambio total",
  valorUnitario: "Precio cada uno",
  valorAjuste: "Valor del cambio",
  documentoSoporte: "Papel de soporte",
  stockAlMomento: "Había al borrar",
  valorInventarioPerdido: "Dinero perdido en stock",
  ultimaVenta: "Última vez vendido",
  // Seguridad / Admin
  intento: "Número de intento",
  intentosRestantes: "Intentos que le quedan",
  motor: "Tipo de operación",
  origin: "Origen del registro",
  accion: "Tipo de acción",
  empleadoNombre: "Nombre del empleado",
  emailSolicitado: "Correo de recuperación",
  auditoria_detail: "Detalle de auditoría",
  // Personal
  empleadoId: "Código de referencia",
  cedula: "Cédula",
  rol: "Puesto de trabajo",
  tipoContrato: "Tipo de contrato",
  salarioInicial: "Sueldo",
  horario: "Horario asignado",
  fechaIngreso: "Fecha que entró",
  porcentajeAumento: "Aumento de sueldo",
  periodoEfectivo: "Desde cuándo",
  justificacion: "Explicación",
  horaEntrada: "Llegada",
  horaEsperada: "Debía llegar a las",
  minutosRetraso: "Minutos tarde",
  puntual: "Llegó a tiempo",
  horaSalida: "Salida",
  horaEsperadaSalida: "Debía salir a las",
  totalHoras: "Horas trabajadas",
  minutosExtra: "Minutos adicionales",
  metodo: "Cómo marcó entrada",
  // Tareas
  titulo: "Título de tarea",
  descripcionTarea: "Explicación de tarea",
  prioridad: "Urgencia",
  fechaLimite: "Para entregar el",
  assignadoA: "Encargado",
  cantidadAsignados: "Personas asignadas",
  impacto: "Consecuencia / Impacto",
  actorRole: "Rango de quien realizó la acción",
  // Fidelización
  clienteId: "ID del cliente",
  puntosOtorgados: "Puntos ganados",
  puntosRedimidos: "Puntos canjeados",
  nuevoSaldo: "Saldo final de puntos",
  premio: "Premio / Recompensa",
  ajuste: "Valor del ajuste manual",
  montoCompra: "Valor de la compra",
};

/** Formato amigable para un valor según su clave */
const formatValue = (key: string, val: unknown): string => {
  if (val === null || val === undefined) return "";

  // Booleanos legibles
  if (typeof val === "boolean") {
    const boolLabels: Record<string, [string, string]> = {
      puntual: ["Sí", "No"],
      cuentaBloqueada: ["Sí (bloqueada)", "No"],
    };
    const labels = boolLabels[key];
    return labels ? (val ? labels[0] : labels[1]) : val ? "Sí" : "No";
  }

  // Formatea montos y números relevantes como moneda
  if (
    [
      "montoInicial",
      "montoCierre",
      "diferencia",
      "precio",
      "costo",
      "subtotalBruto",
      "subtotalNeto",
      "subtotal",
      "descuento",
      "impuesto",
      "total",
      "salarioInicial",
      "valorUnitario",
      "valorAjuste",
      "valorInventarioPerdido",
      "promedioVenta",
      "montoReal",
      "montoSalida",
      "vueltoMonto",
      "faltanteMonto",
      "montoRealIndicado",
      "monto",
      "montoDescuento",
      "precioOriginal",
      "precioFinal",
      "totalVenta",
      "descuentoGeneral",
      "descuentoItems",
      "servicio",
      "montoCompra",
      "ajuste",
      "precioUnitario",
      "valorLinea",
      "totalCarrito",
    ].includes(key)
  ) {
    const n = Number(val);
    if (!isNaN(n)) return money(val);
  }

  // Arrays de strings (ej: ajustes de producto)
  if (Array.isArray(val)) {
    return val.join(", ");
  }

  // Método de pago (labels del catálogo compartido)
  if (key === "metodoPago") {
    return getDefaultPaymentMethodLabel(String(val));
  }

  // Estado de la alerta del Botón de Pánico
  if (key === "estadoAlerta") {
    const estados: Record<string, string> = {
      whatsapp_abierto: "WhatsApp abierto en el dispositivo",
      sin_contacto: "Sin contacto configurado (no se envió)",
    };
    return estados[String(val)] || String(val);
  }

  // Estado de asistencia
  if (key === "estado") {
    const estados: Record<string, string> = {
      puntual: "Puntual ✓",
      tarde: "Tarde",
      ausente: "Ausente",
    };
    return estados[String(val)] || String(val);
  }

  // Motor de ejecución (admin_action, etc.)
  if (key === "motor") {
    const motores: Record<string, string> = {
      admin_action: "Acción administrativa",
      system: "Sistema automático",
      scheduler: "Tarea programada",
      pos_printer: "Impresora térmica",
      web_browser: "Navegador web",
      reset_token: "Token de seguridad",
    };
    return motores[String(val)] || String(val);
  }

  // Origen del evento
  if (key === "origin") {
    const origenes: Record<string, string> = {
      auditoria_detail: "Detalle de auditoría",
      auditoria: "Auditoría",
      personal: "Personal",
      caja: "Caja",
      inventario: "Inventario",
      dashboard: "Panel principal",
      auth: "Autenticación",
      seguridad: "Seguridad",
      settings: "Ajustes de usuario",
    };
    return origenes[String(val)] || String(val);
  }

  // Traducción de acciones internas
  if (key === "accion") {
    const acciones: Record<string, string> = {
      solicitud_recuperacion: "Solicitud de recuperación",
      cambio_password_recuperacion: "Cambio de contraseña",
      regeneracion_carnet: "Regeneración de carnet",
      impresion_carnet: "Impresión de carnet",
    };
    return acciones[String(val)] || String(val);
  }

  // Tipo de acción (habilitar / deshabilitar)
  if (key === "accion") {
    const acciones: Record<string, string> = {
      habilitar: "Habilitar acceso",
      deshabilitar: "Deshabilitar acceso",
      bloquear_ip: "Bloqueo de IP",
      imprimir_credencial: "Impresión de carnet",
      regenerar_credencial: "Regeneración de carnet",
      deshabilitar_credencial: "Anulación de carnet",
      recuperar_solicitud: "Solicitud de recuperación",
      restablecer_contraseña: "Cambio por olvido",
      solicitar_cambio_propio: "Cambio desde perfil",
    };
    return acciones[String(val)] || String(val);
  }

  // IP local → texto amigable
  if (key === "ip") {
    const v = String(val);
    if (v === "local" || v === "localhost" || v === "127.0.0.1")
      return "Red local";
  }

  return String(val);
};

/** Campos internos/técnicos que no se deben mostrar en el modal de detalles */
const HIDDEN_FIELDS = new Set([
  "cajaId",
  "productoId",
  "empleadoId",
  "tareaId",
  "asistenciaId",
  "businessId",
  "sessionId",
  "userAgent",
  "socialProvider",
  "provider",
]);

/**
 * Retorna un arreglo de { label, value } con los detalles formateados
 * para mostrar en el modal de detalle completo.
 * Filtra campos internos (IDs) y arrays sin formato.
 */
export const formatDetailsReadable = (
  details: Record<string, unknown> | null | undefined,
): { label: string; value: string }[] => {
  if (!details) return [];
  return Object.entries(details)
    .filter(([key, val]) => {
      if (HIDDEN_FIELDS.has(key)) return false;
      // Omitir arrays crudos que ya están en otros campos formateados
      if (
        Array.isArray(val) &&
        !["asignadoA", "ajustesProductos"].includes(key)
      )
        return false;
      return true;
    })
    .map(([key, val]) => ({
      label:
        FIELD_LABELS[key] ||
        key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1"),
      value: formatValue(key, val),
    }));
};

/** Etiquetas legibles para las acciones */
const ACTION_LABELS: Record<string, string> = {
  login: "Inicio de sesión",
  login_failed: "Intento de acceso fallido",
  logout: "Cierre de sesión",
  venta_creada: "Nueva venta realizada",
  caja_venta_completada: "Caja: Venta cobrada",
  caja_venta_credito: "Caja: Venta a crédito",
  caja_descuento_aplicado: "Caja: Descuento global",
  caja_servicio_registrado: "Caja: Propina registrada",
  caja_item_ajustado: "Caja: Cambio de precio",
  caja_producto_carrito_eliminado: "Producto eliminado del carrito",
  caja_venta_espera_eliminada: "Venta en espera eliminada",
  caja_producto_venta_espera_eliminado: "Producto eliminado de venta en espera",
  seguridad_panico_activado: "Alerta de seguridad activada",
  venta_eliminada: "Venta eliminada",
  caja_venta_borrada: "Eliminación de caja",
  caja_retiro_registrado: "Retiro de efectivo",
  caja_gasto_registrado: "Gasto registrado",
  caja_ingreso_registrado: "Ingreso de dinero",
  venta_editada: "Venta modificada",
  caja_abierta: "Caja abierta para el turno",
  caja_cerrada: "Cierre de caja (Diferencia de dinero)",
  caja_punto_creado: "Nuevo punto de venta",
  caja_punto_editado: "Edición de punto de venta",
  caja_punto_eliminado: "Eliminación de caja",
  caja_arqueo_iniciado: "Inicio de arqueo físico",
  caja_arqueo_fallido: "Conteo de efectivo fallido",
  caja_reporte_parcial_pedido: "Solicitud de Auditoría Parcial",
  caja_reporte_parcial_visto: "Auditoría Parcial visualizada",
  caja_reporte_parcial_compartido: "Reporte parcial compartido",
  producto_creado: "Nuevo producto registrado",
  producto_editado: "Producto actualizado",
  producto_eliminado: "Producto eliminado del sistema",
  stock_ajustado: "Ajuste de inventario",
  categoria_creada: "Nueva categoría añadida",
  empleado_creado: "Nuevo empleado añadido",
  empleado_editado: "Datos de empleado actualizados",
  empleado_eliminado: "Empleado retirado",
  salario_actualizado: "Sueldo modificado",
  permisos_modificados: "Permisos de acceso cambiados",
  tarea_creada: "Nueva tarea asignada",
  tarea_editada: "Tarea modificada",
  tarea_eliminada: "Tarea eliminada",
  tarea_completada: "Tarea terminada",
  tarea_estado_cambiado: "Cambio de estado en tarea",
  check_in: "Registro de entrada",
  check_out: "Registro de salida",
  asistencia_admin: "Asistencia registrada por admin",
  // IA / Higiene / Otros
  ia_detection: "Aviso: Patrón de efectivo extraño",
  check_limpieza: "Registro de limpieza realizado",
  metricas_consultadas: "Revisión de métricas del negocio",
  pedido_online: "Pedido recibido por internet",
  nueva_calificacion: "Calificación de cliente recibida",
  calificacion_archivada: "Calificación archivada",
  session_hijack_detected: "Acceso sospechoso en cuenta",
  stock_manual_update: "Ajuste de inventario",
  employee_enabled: "Empleado habilitado",
  employee_disabled: "Empleado inhabilitado",
  credential_disabled: "Credencial inhabilitada",
  credential_regenerated: "Regeneración de carnet",
  credential_printed: "Impresión de carnet",
  forgot_password_request: "Solicitud de recuperación de contraseña",
  admin_action: "Acción administrativa",
  session_revoked: "Expulsión: Sesión cerrada por seguridad",
  ip_blocked: "Acceso bloqueado por seguridad",
  // Facturación / Caja
  facturacion_venta_anulada: "Venta anulada",
  facturacion_devolucion_procesada: "Devolución de venta procesada",
  tesoreria_movimiento_anulado: "Movimiento de tesorería anulado",
  // Créditos
  credito_abono_registrado: "Abono a deuda registrado",
  // Fidelización
  fidelizacion_config_guardada: "Configuración de puntos guardada",
  fidelizacion_premio_creado: "Premio de fidelización creado",
  fidelizacion_premio_editado: "Premio de fidelización editado",
  fidelizacion_premio_eliminado: "Premio de fidelización eliminado",
  fidelizacion_sorteo_realizado: "Sorteo de fidelización realizado",
  // Inventario
  inventario_stock_agotado: "Stock agotado (manual)",
  inventario_stock_repuesto: "Stock repuesto",
  inventario_precios_masivos: "Actualización masiva de precios",
  inventario_conteo_ajustado: "Conteo físico de inventario",
  inventario_conteo_exportado: "Reporte de conteo exportado",
  // Personal
  personal_empleado_creado: "Nuevo empleado añadido",
  personal_empleado_editado: "Datos de empleado actualizados",
  personal_empleado_eliminado: "Empleado eliminado del sistema",
  // Tareas
  personal_tarea_creada: "Nueva tarea asignada",
  personal_tarea_editada: "Tarea modificada",
  personal_tarea_eliminada: "Tarea eliminada",
  personal_tarea_estado_cambiado: "Cambio de estado en tarea",
  // Asistencia
  personal_asistencia_entrada: "Registro de entrada",
  personal_asistencia_salida: "Registro de salida",
  // Nómina
  nomina_empleado_liquidado: "Nómina / Liquidación guardada",
  // Turnos
  personal_turno_asignado: "Turno asignado al empleado",
  // Vacantes
  personal_vacante_creada: "Vacante publicada",
  personal_vacante_editada: "Vacante actualizada",
  personal_vacante_eliminada: "Vacante eliminada",
  personal_postulacion_estado_cambiado: "Estado de postulación cambiado",
  // Buzón
  personal_buzon_mensaje_publicado: "Mensaje anónimo en buzón del equipo",
  // Higiene
  higiene_area_creada: "Área de higiene creada",
  higiene_area_editada: "Área de higiene editada",
  higiene_area_eliminada: "Área de higiene eliminada",
  higiene_item_creado: "Ítem de higiene creado",
  higiene_item_editado: "Ítem de higiene editado",
  higiene_item_eliminado: "Ítem de higiene eliminado",
  higiene_registro_guardado: "Registro de higiene guardado",
  // Tienda
  tienda_pedido_estado_cambiado: "Pedido actualizado",
  tienda_pedido_anulado: "Pedido anulado",
  tienda_pedido_devolucion: "Devolución de pedido",
  tienda_config_guardada: "Configuración de tienda guardada",
};

/** Devuelve la etiqueta legible de una acción */
export const getActionLabel = (action: string): string =>
  ACTION_LABELS[action] || action.replace(/_/g, " ");
